import type { ServiceCenterBookingMeetingDetails, ServiceCenterBookingOrganizer } from "../home/serviceCenterHomeTypes"

export type ResourceOccupancyAvailability = Readonly<{
    startTime: string
    endTime: string
    availabilityView: string
}>

// Every order field is optional and follows the SC bookings shape until the backend publishes the real contract.
export type ResourceOccupancyOrder = Readonly<{
    id: number
    checkIn?: Readonly<{ checkInTime?: string | null; isValid?: boolean }>
    meetingDetails?: Partial<ServiceCenterBookingMeetingDetails> & Readonly<{ isPrivate?: boolean }>
    organizers?: ReadonlyArray<ServiceCenterBookingOrganizer>
    orderSummary?: Readonly<{
        participants?: number
        hasCatering?: boolean
        hasBooking?: boolean
        hasAudioVideoEquipment?: boolean
    }>
    bookingDetails?: Readonly<{
        resources?: ReadonlyArray<
            Readonly<{
                id: number
                room?: Readonly<{ tableSetup?: ResourceOccupancyTableSetup }>
            }>
        >
    }>
}>

export type ResourceOccupancyTableSetup = Readonly<{ key: string; name: string; imageUrl: string }>

export type ResourceOccupancyRoom = Readonly<{
    floor?: Readonly<{ name: string; value: number }>
    seats?: number
    avEquipments?: ReadonlyArray<Readonly<{ id: number; name: string }>>
    tableSetups?: ReadonlyArray<ResourceOccupancyTableSetup>
}>

export type ResourceOccupancyResource = Readonly<{
    id: number
    uid: string
    name?: string
    resourceNumber?: string
    buffer?: number
    room?: ResourceOccupancyRoom
    outOfService?: Readonly<{ active: boolean; message: string }>
}>

export type ResourceOccupancyItem = Readonly<{
    resource: ResourceOccupancyResource
    availabilities?: ReadonlyArray<ResourceOccupancyAvailability>
    orders?: ReadonlyArray<ResourceOccupancyOrder>
}>

export type ResourceOccupancyResponse = Readonly<{
    availabilityDetails?: Readonly<{ startTime: string; endTime: string; availabilityViewInterval: number }>
    summary?: Readonly<{ messages: ReadonlyArray<string> }>
    resources: ReadonlyArray<ResourceOccupancyItem>
}>

export enum MeetingRoomStatus {
    BOOKED = "BOOKED",
    AVAILABLE = "AVAILABLE",
    NOT_CHECKED_IN = "NOT_CHECKED_IN",
    OUT_OF_SERVICE = "OUT_OF_SERVICE",
}

export enum MeetingRoomNowStatus {
    IN_USE = "IN_USE",
    FREE = "FREE",
}

export enum MeetingRoomStatusFilter {
    ALL = "ALL",
    BOOKED = "BOOKED",
    AVAILABLE = "AVAILABLE",
}

export enum MeetingRoomsView {
    STATUS = "STATUS",
    BY_HOUR = "BY_HOUR",
    BY_ROOM = "BY_ROOM",
}

export enum MeetingRoomBookingState {
    IN_PROGRESS = "IN_PROGRESS",
    FINISHED = "FINISHED",
    UPCOMING = "UPCOMING",
}

export type MeetingRoomBusyInterval = Readonly<{
    startMillis: number
    endMillis: number
}>

export type MeetingRoomBooking = Readonly<{
    orderId: number
    // undefined while the backend does not send check-in for the order
    isCheckedIn?: boolean
    meetingName?: string
    startDateTime?: string
    endDateTime?: string
    timeRange?: MeetingRoomBusyInterval
    organizers: ReadonlyArray<ServiceCenterBookingOrganizer>
    participants?: number
    hasCatering?: boolean
    hasBooking?: boolean
    hasAudioVideoEquipment?: boolean
    tableSetupName?: string
    isPrivate?: boolean
    isExternal?: boolean
}>

export type MeetingRoomRow = Readonly<{
    id: number
    name: string
    floorName: string
    seats?: number
    equipment: ReadonlyArray<string>
    isOutOfService: boolean
    outOfServiceMessage?: string
    busyIntervals: ReadonlyArray<MeetingRoomBusyInterval>
    unbookedBusyIntervals: ReadonlyArray<MeetingRoomBusyInterval>
    bookings: ReadonlyArray<MeetingRoomBooking>
}>

export type MeetingRoomHourRange = Readonly<{
    firstHour: number
    lastHour: number
}>

export type MeetingRoomsOverview = Readonly<{
    summaryMessages: ReadonlyArray<string>
    rooms: ReadonlyArray<MeetingRoomRow>
    floorNames: ReadonlyArray<string>
}>

export const ALL_FLOORS = "ALL_FLOORS"

export enum MeetingRoomFreeLineKind {
    OUT_OF_SERVICE = "OUT_OF_SERVICE",
    FREE_FROM = "FREE_FROM",
    FREE_BETWEEN = "FREE_BETWEEN",
    FREE_UNTIL = "FREE_UNTIL",
    FREE_REST_OF_DAY = "FREE_REST_OF_DAY",
    BOOKED_REST_OF_DAY = "BOOKED_REST_OF_DAY",
    NO_FREE_TIME = "NO_FREE_TIME",
}

export type MeetingRoomFreeLine = Readonly<{
    kind: MeetingRoomFreeLineKind
    timeRange?: MeetingRoomBusyInterval
}>

export type MeetingRoomTableRow = Readonly<{
    room: MeetingRoomRow
    status: MeetingRoomStatus
    isBookable: boolean
    booking?: MeetingRoomBooking
    isInUseNow: boolean
    liveBooking?: MeetingRoomBooking
    freeLine: MeetingRoomFreeLine
    laterWindows: ReadonlyArray<MeetingRoomBusyInterval>
}>

export type MeetingRoomStatusCounts = Record<MeetingRoomStatusFilter, number>

export type HourOccupancy = Readonly<{
    hour: number
    timeRange: MeetingRoomBusyInterval
    bookedCount: number
}>

export type MeetingRoomBookingItem = Readonly<{
    room: MeetingRoomRow
    booking: MeetingRoomBooking
    timeRange: MeetingRoomBusyInterval
    state: MeetingRoomBookingState
}>

export type MeetingRoomExportRow = Readonly<{
    room: MeetingRoomRow
    status?: MeetingRoomStatus
    item?: MeetingRoomBookingItem
}>

export type MeetingRoomHourGroup = Readonly<{
    hour: number
    timeRange: MeetingRoomBusyInterval
    items: ReadonlyArray<MeetingRoomBookingItem>
    freeRooms: ReadonlyArray<MeetingRoomRow>
}>

export type MeetingRoomTablePagination = Readonly<{
    pageIndex: number
    pageCount: number
    pageSize: number
}>

export type MeetingRoomRoomGroup = Readonly<{
    room: MeetingRoomRow
    items: ReadonlyArray<MeetingRoomBookingItem>
}>

export type MeetingRoomDrawerSelection = Readonly<{
    roomId: number
    orderId?: number
}>

export enum MeetingRoomTimelineMode {
    FROM_NOW = "FROM_NOW",
    WHOLE_DAY = "WHOLE_DAY",
}

export type MeetingRoomTimelineSegment =
    | Readonly<{ timeRange: MeetingRoomBusyInterval; item: MeetingRoomBookingItem; isOccupied: true }>
    | Readonly<{ timeRange: MeetingRoomBusyInterval; item?: undefined; isOccupied: boolean }>
